import React, {useEffect, useRef, useState} from 'react';
import {Form, Input} from 'antd';
import {CheckCircleOutlined, CloseCircleOutlined} from '@ant-design/icons';
import i18n, {t} from 'i18n';
import {BolDataV3, BolStop, TaskGroupType} from './types/bolTypes';
import {useMutation} from '@apollo/client';
import {UPDATE_TMS_TASK_BOL_INFO, BolInfoUpdateInput} from '../../../../../../graphql/mutations/tms/updateTmsTaskBolInfo';
import {useWarehouseContext} from '../../../../../../contexts/WarehouseContext';
import {getAbbrSortCode, isHandoverThirdPartyBol} from './ts/bolUtils';
import {
    SaveStatus,
    SHIP_TO_FIELD_BASES,
    ShipToFieldBase,
    SHIP_TO_REQUIRED_FIELDS,
    CARRIER_FIELD_BASES,
    CarrierFieldBase,
    CARRIER_REQUIRED_FIELDS,
    CARRIER_FIELD_LABEL_KEYS,
    CARRIER_FIELD_PLACEHOLDER_KEYS,
    buildShipToFieldMeta,
    buildStopFormFields,
    buildSealFieldName,
    getSealSegments
} from './ts/bolEditorShared';

const STOP_POSITION_LABELS: Record<number, string> = {
    1: '1st STOP',
    2: '2nd STOP',
    3: '3rd STOP'
};

const getStopLabelKey = (index: number, stopCount: number): string => {
    if (index === 0) return 'PICK UP';
    if (index === stopCount - 1 && stopCount > 1) return 'DROP OFF';
    return STOP_POSITION_LABELS[index] ?? `${index}th STOP`;
};

interface BolFormEditorProps {
    bolData: BolDataV3;
    onDataChange: (data: BolDataV3) => void;
    taskGroupType?: TaskGroupType;
}

const BolFormEditor: React.FC<BolFormEditorProps> = ({
    bolData,
    onDataChange,
    taskGroupType
}) => {
    const [form] = Form.useForm();
    const editorScrollRef = useRef<HTMLDivElement>(null);
    const fieldOriginalValuesRef = useRef<Record<string, any>>({});
    const initialBolDataRef = useRef<BolDataV3 | null>(null);
    const [updateBolInfo] = useMutation(UPDATE_TMS_TASK_BOL_INFO);
    const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
    const saveStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 获取当前登录仓库信息
    const { currentWarehouse } = useWarehouseContext();

    // 初始化时保存深拷贝的初始数据
    initialBolDataRef.current ??= structuredClone(bolData);

    /** 显示保存状态提示，1 秒后自动消失 */
    const showSaveStatus = (status: SaveStatus) => {
        setSaveStatus(status);
        if (saveStatusTimeoutRef.current) {
            clearTimeout(saveStatusTimeoutRef.current);
        }
        saveStatusTimeoutRef.current = setTimeout(() => {
            setSaveStatus(null);
        }, 1000);
    };

    // 根据当前语言决定是否在字段名和"必填"之间加空格
    const getRequiredMessage = (fieldKey: string): string => {
        const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
        const fieldName = String(t(fieldKey));
        const requiredText = String(t('components.input.required'));
        const space = currentLang === 'zh' ? '' : ' ';
        return fieldName + space + requiredText;
    };

    // 获取站点标签的国际化文本
    const getStopLabel = (label: string): string => {
        switch (label) {
            case 'PICK UP':
                return String(t('tms.task.bol.stopLabels.pickUp'));
            case 'DROP OFF':
                return String(t('tms.task.bol.stopLabels.dropOff'));
            case '1st STOP':
                return String(t('tms.task.bol.stopLabels.stop1'));
            case '2nd STOP':
                return String(t('tms.task.bol.stopLabels.stop2'));
            case '3rd STOP':
                return String(t('tms.task.bol.stopLabels.stop3'));
            default:
                return label;
        }
    };

    // 交接+三方：除始发站外，联系人姓名/电话非必填（不显示 *、允许清空保存）
    const isOptionalContactField = (fieldBase: ShipToFieldBase, stopIndex: number): boolean =>
        isHandoverThirdPartyBol(bolData) && stopIndex > 0
        && (fieldBase === SHIP_TO_FIELD_BASES[2] || fieldBase === SHIP_TO_FIELD_BASES[3]);

    // 用 warehouse id 比较，不使用sortingCode比较，避免 LA/LAX 等 sortCode 命名差异
    const warehouseIdMatches = (stopId: string) =>
        currentWarehouse?.id != null && String(stopId) === String(currentWarehouse.id);

    // 检查指定 stop 的地址信息是否可编辑（本站发出/到达本站，支持环形路线）
    const canEditStop = (stop: BolStop, stopIndex: number): boolean => {
        if (currentWarehouse?.id == null) return false;

        const stops = bolData.stops;
        const lastIdx = stops.length - 1;
        const isStart = warehouseIdMatches(stops[0].shipToId);
        const isEnd = warehouseIdMatches(stops[lastIdx].shipToId);
        const minIdx = stops.findIndex(s => warehouseIdMatches(s.shipToId));

        if (taskGroupType === TaskGroupType.Departure) return isStart;
        if (taskGroupType === TaskGroupType.Arrival) return isEnd ? stopIndex === lastIdx : minIdx !== -1 && stopIndex >= minIdx;
        return isStart || (isEnd && stopIndex === lastIdx) || (minIdx !== -1 && stopIndex >= minIdx);
    };

    /**
     * 生成所有站点信息
     */
    const generateStopList = (): Array<{ label: string; stopInfo: BolStop; index: number }> => {
        if (bolData.stops.length === 0) return [];
        const stopCount = bolData.stops.length;
        return bolData.stops.map((stop, index) => ({
            label: getStopLabelKey(index, stopCount),
            stopInfo: stop,
            index
        }));
    };

    const stopList = generateStopList();
    // 只有一个 stop 时，卡片占满整行、字段改为一行两个
    const isSingleStop = stopList.length === 1;
    const sealSegments = getSealSegments(bolData.stops, bolData.taskType);

    /** 更新指定 stop：调用方传更新函数，写真实属性 */
    const updateStop = (stopIndex: number, update: (stop: BolStop) => BolStop): BolDataV3 => ({
        ...bolData,
        stops: bolData.stops.map((stop, idx) => (idx === stopIndex ? update(stop) : stop))
    });

    /**
     * 保存到数据库：input 只包含本次变更的字段，后端只更新实际传入的字段
     * @param input 变更字段补丁（写真实属性，无字段名字符串）
     * @param stopIndex 站点级字段所在 stop 下标；任务级字段（承运商信息）传 null
     */
    const saveToDatabase = async (input: BolInfoUpdateInput, stopIndex: number | null = null) => {
        if (!bolData.taskId) {
            console.warn('Missing taskId for saving BOL info');
            return;
        }

        const variables: Record<string, unknown> = {
            taskId: Number.parseInt(bolData.taskId),
            input
        };
        if (stopIndex !== null) {
            variables.segment = bolData.stops[stopIndex].segment;
        }

        try {
            const { data } = await updateBolInfo({ variables });
            // 成功与否直接取自返回值：后端更新失败会抛异常走 catch，返回空同样视为失败
            const ok = Boolean(data?.updateTmsTaskBolInfo);
            if (ok) {
                // 保存成功：把补丁合并进本地预览数据（null 统一落为 ''）
                const patch: Partial<Record<keyof BolInfoUpdateInput, string>> = {};
                (Object.keys(input) as Array<keyof BolInfoUpdateInput>).forEach((key) => {
                    patch[key] = input[key] ?? '';
                });
                const updatedData = stopIndex === null
                    ? { ...bolData, carrier: { ...bolData.carrier, ...patch } }
                    : updateStop(stopIndex, stop => ({ ...stop, ...patch }));
                onDataChange(updatedData);
                initialBolDataRef.current = structuredClone(updatedData);
            } else {
                // 保存失败：预览还原为初始值（表单值由 useEffect 按 bolData 回写）
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
            }
            showSaveStatus(ok ? SaveStatus.Success : SaveStatus.Error);
        } catch (error) {
            // 保存异常：预览还原为初始值（表单值由 useEffect 按 bolData 回写）
            onDataChange(structuredClone(initialBolDataRef.current || bolData));
            showSaveStatus(SaveStatus.Error);
        }
    };

    /**
     * 承运商信息：输入时实时更新 BOL 预览
     * @param fieldName
     */
    const handleCarrierInputChange = (fieldName: string) => () => {
        const fieldValue = form.getFieldValue(fieldName);
        // 保存原始值（第一次 onChange 时）
        if (!(fieldName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[fieldName] = bolData.carrier[fieldName as keyof typeof bolData.carrier];
        }
        const updatedData = structuredClone(bolData);
        updatedData.carrier[fieldName as keyof typeof updatedData.carrier] = fieldValue;
        onDataChange(updatedData);
    };

    // 处理承运商信息字段失焦事件 - 保存到数据库
    const handleCarrierInputBlur = (fieldName: string) => () => {
        const fieldValue = form.getFieldValue(fieldName);
        if (!(fieldName in fieldOriginalValuesRef.current)) {
            return;
        }

        const originalValue = fieldOriginalValuesRef.current[fieldName];

        // 如果是必填项且被清空了，不保存
        if (CARRIER_REQUIRED_FIELDS.includes(fieldName as CarrierFieldBase) && !fieldValue) {
            return;
        }

        // 只有当值改变时才执行保存
        if (fieldValue !== originalValue) {
            saveToDatabase({ [fieldName]: fieldValue } as BolInfoUpdateInput);
        }

        // 清除该字段的原始值记录
        delete fieldOriginalValuesRef.current[fieldName];
    };

    /**
     * 封签号可编辑逻辑
     * 环形路线如 ORD-DFW-HOU-EWR-ORD，seal 分四段：ORD-DFW | DFW-HOU | HOU-EWR | EWR-ORD
     * - 本站发出：可编辑「从本站出发的段」及之后所有段。ORD 可改全部 4 段，DFW 可改后 3 段
     * - 到达本站：当前站是终点时，最后一段的 seal 是上一站设置的，本站无权限
     */
    const canEditSealField = (stopIndex: number): boolean => {
        if (!currentWarehouse?.id) return false;
        const stop = bolData.stops[stopIndex];
        if (!stop) return false;

        const lastStop = bolData.stops[bolData.stops.length - 1];
        if (!lastStop) return false;

        // 到达本站：当前站是路线终点时，最后一段(如 EWR-ORD)的 seal 在 EWR 设置，ORD 无权限
        if (taskGroupType === TaskGroupType.Arrival && warehouseIdMatches(lastStop.shipToId)) {
            return false;
        }

        // 本站发出：可编辑「从本站出发的段」及之后所有段
        const firstSegmentFromCurrentStation = bolData.stops.findIndex(s => warehouseIdMatches(s.shipFromId));
        if (firstSegmentFromCurrentStation === -1) return false;
        return stopIndex >= firstSegmentFromCurrentStation;
    };

    /**
     * 封签号：输入时实时更新 BOL 预览
     * @param stopIndex
     */
    const handleSealNumberInputChange = (stopIndex: number) => {
        const fieldName = buildSealFieldName(stopIndex);
        const fieldValue = form.getFieldValue(fieldName);

        if (!(fieldName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[fieldName] = bolData.stops[stopIndex]?.sealNumber ?? '';
        }

        if (!canEditSealField(stopIndex)) {
            return;
        }

        const normalizedValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '');
        onDataChange(updateStop(stopIndex, stop => ({ ...stop, sealNumber: normalizedValue })));
    };

    /**
     * 封签号：失焦时保存到数据库
     * @param stopIndex
     */
    const handleSealNumberInputBlur = (stopIndex: number) => {
        const fieldName = buildSealFieldName(stopIndex);
        const fieldValue = form.getFieldValue(fieldName);
        const originalValue = fieldOriginalValuesRef.current[fieldName] ?? bolData.stops[stopIndex]?.sealNumber ?? '';

        if (fieldValue !== originalValue) {
            if (!canEditSealField(stopIndex)) {
                form.setFieldValue(fieldName, originalValue);
                setTimeout(() => form.setFieldValue(fieldName, originalValue), 0);
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
                delete fieldOriginalValuesRef.current[fieldName];
                return;
            }
            const valueToSave = (fieldValue == null || String(fieldValue).trim() === '') ? null : fieldValue;
            saveToDatabase({ sealNumber: valueToSave }, stopIndex);
        }

        delete fieldOriginalValuesRef.current[fieldName];
    };

    /**
     * 站点信息：输入时实时更新 BOL 预览
     * @param fieldBase
     * @param stopIndex
     */
    const handleStopInputChange = (fieldBase: ShipToFieldBase, stopIndex: number) => {
        const { formName, fieldKey } = buildShipToFieldMeta(fieldBase, stopIndex);
        const stop = bolData.stops[stopIndex];
        // 无权限时直接返回，防止 F12 移除 disabled 后通过 onChange 修改预览数据
        if (!stop || !canEditStop(stop, stopIndex)) return;

        const fieldValue = form.getFieldValue(formName);

        if (!(formName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[formName] = bolData.stops[stopIndex][fieldKey];
        }

        const normalizedValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '');
        onDataChange(updateStop(stopIndex, stop => ({ ...stop, [fieldKey]: normalizedValue } as BolStop)));
    };

    /**
     * 站点信息：失焦时保存到数据库
     * @param fieldBase
     * @param stopIndex
     */
    const handleStopInputBlur = (fieldBase: ShipToFieldBase, stopIndex: number) => {
        const { formName } = buildShipToFieldMeta(fieldBase, stopIndex);
        const fieldValue = form.getFieldValue(formName);
        const originalValue = fieldOriginalValuesRef.current[formName];

        if (SHIP_TO_REQUIRED_FIELDS.includes(fieldBase) && !fieldValue && !isOptionalContactField(fieldBase, stopIndex)) {
            return;
        }

        if (fieldValue !== originalValue) {
            const stop = bolData.stops[stopIndex];
            // 无权限时：还原表单值、还原预览数据并返回，不发起保存请求
            if (!stop || !canEditStop(stop, stopIndex)) {
                const { fieldKey } = buildShipToFieldMeta(fieldBase, stopIndex);
                form.setFieldValue(formName, originalValue ?? stop?.[fieldKey] ?? '');
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
                delete fieldOriginalValuesRef.current[formName];
                return;
            }

            // stops[0]（始发站）在库里没有独立段行：其信息即下一段的 shipFrom，跳过 segment=0 的无效写入
            if (stopIndex > 0) {
                saveToDatabase({ [fieldBase]: fieldValue } as BolInfoUpdateInput, stopIndex);
            }

            // 同步写入下一段的 shipFrom 冗余字段
            if (stopIndex < bolData.stops.length - 1) {
                const nextField = fieldBase.replace('shipTo', 'shipFrom');
                saveToDatabase({ [nextField]: fieldValue } as BolInfoUpdateInput, stopIndex + 1);
            }
        }

        delete fieldOriginalValuesRef.current[formName];
    };

    useEffect(() => {
        // 设置所有站点的表单字段值
        const formValues: Record<string, any> = {};
        CARRIER_FIELD_BASES.forEach((fb) => {
            formValues[fb] = bolData.carrier[fb as keyof typeof bolData.carrier];
        });

        stopList.forEach((stop) => {
            const fields = buildStopFormFields(stop.index);

            formValues[fields.address.formName] = stop.stopInfo.shipToAddress;
            formValues[fields.contactName.formName] = stop.stopInfo.shipToContactName;
            formValues[fields.contactPhone.formName] = stop.stopInfo.shipToContactPhoneNumber;
        });

        sealSegments.forEach((segment) => {
            formValues[buildSealFieldName(segment.stopIndex)] = segment.sealNumber;
        });


        form.setFieldsValue(formValues);
    }, [bolData, form, stopList, sealSegments]);

    // 清理 timeout
    useEffect(() => {
        return () => {
            if (saveStatusTimeoutRef.current) {
                clearTimeout(saveStatusTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="flex-1 border border-gray-300 rounded bg-white flex flex-col overflow-hidden min-w-[420px]">
            <h3 className="px-5 pt-3 pb-2 bg-white border-b border-gray-200 flex-shrink-0 whitespace-nowrap rounded-t flex items-center justify-between">
                <span>{String(t('tms.task.bol.edit'))}</span>
                {saveStatus === SaveStatus.Success && (
                    <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircleOutlined />
                        {String(t('common.messages.saved'))}
                    </span>
                )}
                {saveStatus === SaveStatus.Error && (
                    <span className="flex items-center gap-1 text-red-600 text-sm">
                        <CloseCircleOutlined />
                        {String(t('tms.task.bol.errors.failedToSave'))}
                    </span>
                )}
            </h3>
            <div ref={editorScrollRef} className="overflow-y-auto flex-1 rounded-b">
                <div className="p-5">
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <div className="flex flex-col gap-4">
                            <div className="border-b pb-4 bol-address-grid">
                                <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.addressInformation'))}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {stopList.map((stop) => {
                                        const fields = buildStopFormFields(stop.index);
                                        const addressValue = stop.stopInfo.shipToAddress;
                                        const cityStateZipValue = stop.stopInfo.shipToCityStateZipcode;
                                        const contactNameValue = stop.stopInfo.shipToContactName;
                                        const contactPhoneValue = stop.stopInfo.shipToContactPhoneNumber;

                                        // 检查是否可以编辑此stop
                                        const canEdit = canEditStop(stop.stopInfo, stop.index);
                                        // 交接+三方：非始发站的联系人姓名/电话非必填
                                        const contactOptional = isOptionalContactField(SHIP_TO_FIELD_BASES[2], stop.index);

                                        const stopFields = (
                                            <>
                                                <Form.Item
                                                    label={String(t('tms.task.bol.address'))}
                                                    name={fields.address.formName}
                                                    initialValue={addressValue}
                                                    style={{ marginBottom: 5 }}
                                                    rules={[{ required: true, message: getRequiredMessage('tms.task.bol.address') }]}
                                                >
                                                    <Input
                                                        placeholder={String(t('tms.task.bol.placeholderAddress'))}
                                                        allowClear
                                                        disabled={!canEdit}
                                                        onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[0], stop.index)}
                                                        onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[0], stop.index)}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={String(t('tms.task.bol.cityStateZip'))}
                                                    name={fields.cityStateZip.formName}
                                                    initialValue={cityStateZipValue}
                                                    style={{ marginBottom: 5 }}
                                                    rules={[{ required: true, message: getRequiredMessage('tms.task.bol.cityStateZip') }]}
                                                >
                                                    <Input
                                                        placeholder={String(t('tms.task.bol.placeholderCityStateZip'))}
                                                        allowClear
                                                        disabled={!canEdit}
                                                        onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[1], stop.index)}
                                                        onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[1], stop.index)}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={String(t('tms.task.bol.contactName'))}
                                                    name={fields.contactName.formName}
                                                    initialValue={contactNameValue}
                                                    style={{ marginBottom: 5 }}
                                                    rules={contactOptional ? undefined : [{ required: true, message: getRequiredMessage('tms.task.bol.contactName') }]}
                                                >
                                                    <Input
                                                        placeholder={String(t('tms.task.bol.placeholderContactName'))}
                                                        allowClear
                                                        disabled={!canEdit}
                                                        onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[2], stop.index)}
                                                        onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[2], stop.index)}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={String(t('tms.task.bol.contactNumber'))}
                                                    name={fields.contactPhone.formName}
                                                    initialValue={contactPhoneValue}
                                                    style={{ marginBottom: 5 }}
                                                    rules={contactOptional ? undefined : [{ required: true, message: getRequiredMessage('tms.task.bol.contactNumber') }]}
                                                >
                                                    <Input
                                                        placeholder={String(t('tms.task.bol.placeholderContactNumber'))}
                                                        allowClear
                                                        disabled={!canEdit}
                                                        onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[3], stop.index)}
                                                        onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[3], stop.index)}
                                                    />
                                                </Form.Item>
                                            </>
                                        );

                                        // 单站点：不用边框卡片，标题占整行、字段直接进外层 grid（一行两个，与承运商信息一致）；
                                        // 多站点：保留原边框卡片、字段竖排
                                        if (isSingleStop) {
                                            return (
                                                <React.Fragment key={`stop-${stop.index}`}>
                                                    <div className="col-span-2 font-semibold text-sm">{getStopLabel(stop.label)}({getAbbrSortCode(stop.stopInfo.shipToSortCode)})</div>
                                                    {stopFields}
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <div key={`stop-${stop.index}`} className="flex flex-col gap-4 border border-gray-200 rounded p-4">
                                                <div className="font-semibold text-sm">{getStopLabel(stop.label)}({getAbbrSortCode(stop.stopInfo.shipToSortCode)})</div>
                                                {stopFields}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="border-b pb-4">
                                <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.carrierInformation'))}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {CARRIER_FIELD_BASES.map((fb) => {
                                        const labelKey = CARRIER_FIELD_LABEL_KEYS[fb];
                                        const placeholderKey = CARRIER_FIELD_PLACEHOLDER_KEYS[fb];
                                        const label = labelKey.startsWith('tms.') ? String(t(labelKey)) : labelKey;
                                        const placeholder = placeholderKey.startsWith('tms.') ? String(t(placeholderKey)) : placeholderKey;
                                        const isRequired = CARRIER_REQUIRED_FIELDS.includes(fb);
                                        return (
                                            <Form.Item
                                                key={fb}
                                                label={label}
                                                name={fb}
                                                rules={isRequired ? [{ required: true, message: getRequiredMessage(labelKey) }] : undefined}
                                                style={{ marginBottom: 5 }}
                                            >
                                                <Input
                                                    placeholder={placeholder}
                                                    allowClear
                                                    onChange={() => handleCarrierInputChange(fb)()}
                                                    onBlur={() => handleCarrierInputBlur(fb)()}
                                                />
                                            </Form.Item>
                                        );
                                    })}
                                </div>
                            </div>

                            {sealSegments.length > 0 && (
                                <div className="border-b pb-4">
                                    <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.sealNumberInformation'))}</h4>
                                    <div className="flex flex-col gap-3 ml-4">
                                        {sealSegments.map((segment, idx) => {
                                            const isSealEditable = canEditSealField(segment.stopIndex);
                                            return (
                                                <div key={segment.stopIndex} className="flex items-center gap-2">
                                                    <div className="flex items-center gap-1 w-[100px] text-sm">
                                                        <span className="flex items-center justify-center">
                                                            {idx + 1}.
                                                        </span>
                                                        <span className="whitespace-normal leading-tight">{segment.label} :</span>
                                                    </div>
                                                    <Form.Item
                                                        name={buildSealFieldName(segment.stopIndex)}
                                                        style={{ marginBottom: 0, flex: 1 }}
                                                        normalize={(v: string) => (v || '').replace(/[^A-Za-z0-9]/g, '')}
                                                    >
                                                        <Input
                                                            placeholder={String(t('tms.task.bol.placeholderSealNumber'))}
                                                            allowClear
                                                            disabled={!isSealEditable}
                                                            onChange={() => handleSealNumberInputChange(segment.stopIndex)}
                                                            onBlur={() => handleSealNumberInputBlur(segment.stopIndex)}
                                                        />
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default BolFormEditor;
